import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface EquipmentSelectorProps {
  clubType: 'driver' | 'iron' | 'wedge';
  value: CurrentClubData;
  onChange: (data: CurrentClubData) => void;
  getPriorityIndicator?: (field: string) => React.ReactNode;
  getPriorityClasses?: (field: string, isEmpty: boolean) => string;
}

interface CurrentClubData {
  brand?: string;
  model?: string;
  loft?: number;
  shaft_flex?: string;
  shaft_model?: string;
  shaft_weight?: number;
  // Additional fields for wedges
  lofts?: string;
  grinds?: string;
  // Auto-populated delta fields
  head_moi?: number;
  head_cg_height?: string;
  head_cg_distance?: string;
  head_volume?: number;
}

interface HeadSpecs {
  brand: string;
  model: string;
  loft?: number;
  moi_combined?: number;
  moi_heel_toe?: number;
  cg_distance?: string;
  cg_height?: string;
  head_volume?: number;
  stock_shaft_brand?: string;
  stock_shaft_model?: string;
  stock_shaft_flex?: string;
  stock_shaft_weight?: number;
}

interface ShaftSpecs {
  flex_options: string[];
  weight_options: number[];
  torque_range?: { min: number; max: number };
  tip_stiffness_options?: string[];
  butt_stiffness_options?: string[];
}

const EquipmentSelector: React.FC<EquipmentSelectorProps> = ({
  clubType,
  value,
  onChange,
  getPriorityIndicator,
  getPriorityClasses
}) => {
  // Dropdown options state
  const [brands, setBrands] = useState<string[]>([]);
  const [models, setModels] = useState<string[]>([]);
  const [lofts, setLofts] = useState<number[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [shaftBrands, setShaftBrands] = useState<string[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [shaftModels, setShaftModels] = useState<string[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [shaftSpecs, setShaftSpecs] = useState<ShaftSpecs | null>(null);

  // UI state
  const [useStockShaft, setUseStockShaft] = useState(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [stockShaftSpecs, setStockShaftSpecs] = useState<{flex?: string, weight?: number, model?: string, brand?: string} | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load brands on mount
  useEffect(() => {
    loadBrands();
    if (!useStockShaft) {
      loadShaftBrands();
    }
  }, [clubType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load models when brand changes
  useEffect(() => {
    if (value.brand) {
      loadModels(value.brand);
    } else {
      setModels([]);
      setLofts([]);
    }
  }, [value.brand]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load lofts when model changes (drivers/wedges only)
  useEffect(() => {
    if (value.brand && value.model && (clubType === 'driver' || clubType === 'wedge')) {
      loadLofts(value.brand, value.model);
    } else if (clubType === 'iron') {
      // For irons, fetch head specs immediately (no loft selection)
      if (value.brand && value.model) {
        loadHeadSpecs(value.brand, value.model);
      }
    }
  }, [value.brand, value.model]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load head specs when loft changes (drivers/wedges)
  useEffect(() => {
    if (value.brand && value.model && value.loft && (clubType === 'driver' || clubType === 'wedge')) {
      loadHeadSpecs(value.brand, value.model, value.loft);
    }
  }, [value.brand, value.model, value.loft]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load shaft models when shaft brand changes
  useEffect(() => {
    if (!useStockShaft && value.shaft_model) {
      // Extract shaft brand from shaft_model if it contains brand info
      // This is a placeholder - we'll need to track shaft brand separately
      const shaftBrand = value.shaft_model.split(' ')[0]; // Simple heuristic
      if (shaftBrand) {
        loadShaftModels(shaftBrand);
      }
    }
  }, [useStockShaft]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadBrands = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/v1/equipment/brands/${clubType}`);
      setBrands(response.data.brands || []);
      setError(null);
    } catch (err) {
      console.error('Error loading brands:', err);
      setError('Failed to load brands');
    } finally {
      setLoading(false);
    }
  };

  const loadModels = async (brand: string) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/equipment/models/${clubType}`,
        { params: { brand } }
      );
      setModels(response.data.models || []);
      setError(null);
    } catch (err) {
      console.error('Error loading models:', err);
      setError('Failed to load models');
    } finally {
      setLoading(false);
    }
  };

  const loadLofts = async (brand: string, model: string) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/equipment/lofts/${clubType}`,
        { params: { brand, model } }
      );
      setLofts(response.data.lofts || []);
      setError(null);
    } catch (err) {
      console.error('Error loading lofts:', err);
      setError('Failed to load lofts');
    } finally {
      setLoading(false);
    }
  };

  const loadHeadSpecs = async (brand: string, model: string, loft?: number) => {
    try {
      setLoading(true);
      const params: any = { brand, model };
      if (loft !== undefined) {
        params.loft = loft;
      }

      const response = await axios.get(
        `${API_BASE_URL}/api/v1/equipment/head-specs/${clubType}`,
        { params }
      );

      const specs: HeadSpecs = response.data;

      // Save stock shaft specs for later use
      setStockShaftSpecs({
        flex: specs.stock_shaft_flex,
        weight: specs.stock_shaft_weight,
        model: specs.stock_shaft_model,
        brand: specs.stock_shaft_brand
      });

      // Auto-populate delta fields ONLY (not shaft fields)
      const updates: any = {
        ...value,
        head_moi: specs.moi_combined,
        head_cg_height: specs.cg_height,
        head_cg_distance: specs.cg_distance,
        head_volume: specs.head_volume
      };

      // DO NOT auto-populate shaft fields
      // Shaft fields should only be populated from prefill data (previous profile)
      // The useStockShaft checkbox only controls read-only state, not auto-population

      onChange(updates);

      setError(null);
    } catch (err) {
      console.error('Error loading head specs:', err);
      setError('Failed to load equipment specs');
    } finally {
      setLoading(false);
    }
  };

  const loadShaftBrands = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/equipment/shaft-brands/${clubType}`
      );
      setShaftBrands(response.data.shaft_brands || []);
    } catch (err) {
      console.error('Error loading shaft brands:', err);
    }
  };

  const loadShaftModels = async (shaftBrand: string) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/equipment/shaft-models`,
        { params: { brand: shaftBrand, category: clubType } }
      );
      setShaftModels(response.data.models || []);
    } catch (err) {
      console.error('Error loading shaft models:', err);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const loadShaftSpecs = async (shaftBrand: string, shaftModel: string) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/equipment/shaft-specs`,
        { params: { brand: shaftBrand, model: shaftModel, category: clubType } }
      );
      setShaftSpecs(response.data);
    } catch (err) {
      console.error('Error loading shaft specs:', err);
    }
  };

  const handleBrandChange = (newBrand: string) => {
    onChange({
      brand: newBrand || undefined,
      model: undefined,
      loft: undefined,
      shaft_flex: undefined,
      shaft_model: undefined,
      shaft_weight: undefined
    });
  };

  const handleModelChange = (newModel: string) => {
    onChange({
      ...value,
      model: newModel || undefined,
      loft: undefined
    });
  };

  const handleLoftChange = (newLoft: string) => {
    onChange({
      ...value,
      loft: newLoft ? parseFloat(newLoft) : undefined
    });
  };

  const handleStockShaftToggle = (checked: boolean) => {
    setUseStockShaft(checked);
    // DO NOT auto-populate shaft fields when toggling checkbox
    // Checkbox only controls read-only state
    // Shaft fields should only contain values from prefill (previous profile)
    // User can manually enter/edit values when checkbox is unchecked
  };

  const defaultInputClass = "mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md border-gray-300 shadow-sm focus:border-green-ink focus:ring-green-ink";

  const getInputClass = (field: string, isEmpty: boolean) => {
    return getPriorityClasses ? getPriorityClasses(field, isEmpty) : defaultInputClass;
  };

  const renderPriorityIndicator = (field: string) => {
    return getPriorityIndicator ? getPriorityIndicator(field) : null;
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Brand Selection */}
        <div>
          <label className="block text-base font-medium text-gray-700 mb-2">
            Brand{renderPriorityIndicator(`equipment_history.current_clubs_${clubType}.brand`)}
          </label>
          <select
            value={value.brand || ''}
            onChange={(e) => handleBrandChange(e.target.value)}
            disabled={loading}
            className={getInputClass(`equipment_history.current_clubs_${clubType}.brand`, !value.brand)}
          >
            <option value="">Select Brand</option>
            {brands.map(brand => (
              <option key={brand} value={brand}>{brand}</option>
            ))}
          </select>
        </div>

        {/* Model Selection */}
        <div>
          <label className="block text-base font-medium text-gray-700 mb-2">
            Model{renderPriorityIndicator(`equipment_history.current_clubs_${clubType}.model`)}
          </label>
          <select
            value={value.model || ''}
            onChange={(e) => handleModelChange(e.target.value)}
            disabled={!value.brand || loading}
            className={getInputClass(`equipment_history.current_clubs_${clubType}.model`, !value.model)}
          >
            <option value="">Select Model</option>
            {models.map(model => (
              <option key={model} value={model}>{model}</option>
            ))}
          </select>
        </div>

        {/* Loft Selection (Driver/Wedge only) */}
        {(clubType === 'driver' || clubType === 'wedge') && (
          <div>
            <label className="block text-base font-medium text-gray-700 mb-2">
              Loft{renderPriorityIndicator(`equipment_history.current_clubs_${clubType}.loft`)}
            </label>
            <select
              value={value.loft || ''}
              onChange={(e) => handleLoftChange(e.target.value)}
              disabled={!value.model || loading}
              className={getInputClass(`equipment_history.current_clubs_${clubType}.loft`, !value.loft)}
            >
              <option value="">Select Loft</option>
              {lofts.map(loft => (
                <option key={loft} value={loft}>{loft}°</option>
              ))}
            </select>
          </div>
        )}

        {/* For wedges, show text input for multiple lofts */}
        {clubType === 'wedge' && (
          <>
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Lofts (if multiple)
              </label>
              <input
                type="text"
                value={value.lofts || ''}
                onChange={(e) => onChange({ ...value, lofts: e.target.value || undefined })}
                placeholder="e.g., 52,56,60"
                className={defaultInputClass}
              />
            </div>

            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">
                Grinds{renderPriorityIndicator(`equipment_history.current_clubs_${clubType}.grinds`)}
              </label>
              <input
                type="text"
                value={value.grinds || ''}
                onChange={(e) => onChange({ ...value, grinds: e.target.value || undefined })}
                placeholder="e.g., M,M,L"
                className={getInputClass(`equipment_history.current_clubs_${clubType}.grinds`, !value.grinds)}
              />
              <p className="mt-1 text-sm text-gray-500">
                Enter grinds separated by commas (e.g., M,M,L for Mid, Mid, Low)
              </p>
            </div>
          </>
        )}
      </div>

      {/* Stock Shaft Toggle */}
      <div className="flex items-center gap-3 bg-blue-50 p-3 rounded">
        <input
          type="checkbox"
          id={`stock-shaft-${clubType}`}
          checked={useStockShaft}
          onChange={(e) => handleStockShaftToggle(e.target.checked)}
          className="w-5 h-5 text-green-ink rounded focus:ring-green-ink"
        />
        <label htmlFor={`stock-shaft-${clubType}`} className="text-base font-medium text-gray-700">
          Using stock shaft (uncheck to select custom shaft)
        </label>
      </div>

      {/* Shaft Selection - Always visible, model read-only when using stock */}
      <div className={`p-4 rounded-lg ${useStockShaft ? 'bg-blue-50' : 'bg-gray-50'}`}>
        <h5 className="font-medium text-gray-900 mb-3">
          {useStockShaft ? 'Stock Shaft Specifications' : 'Custom Shaft'}
        </h5>
        {useStockShaft && (
          <p className="text-sm text-gray-600 mb-3">
            ℹ️ Stock shaft model (manufacturers default). You can select your preferred flex and weight below.
          </p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Shaft Model */}
          <div>
            <label className="block text-base font-medium text-gray-700 mb-2">
              Shaft Model{renderPriorityIndicator(`equipment_history.current_clubs_${clubType}.shaft_model`)}
            </label>
            <input
              type="text"
              value={value.shaft_model || ''}
              onChange={(e) => onChange({ ...value, shaft_model: e.target.value || undefined })}
              placeholder="e.g., Ventus Blue, KBS Tour"
              disabled={useStockShaft}
              className={`${getInputClass(`equipment_history.current_clubs_${clubType}.shaft_model`, !value.shaft_model)} ${useStockShaft ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            />
          </div>

          {/* Shaft Flex - ALWAYS EDITABLE */}
          <div>
            <label className="block text-base font-medium text-gray-700 mb-2">
              Shaft Flex{renderPriorityIndicator(`equipment_history.current_clubs_${clubType}.shaft_flex`)}
            </label>
            <select
              value={value.shaft_flex || ''}
              onChange={(e) => onChange({ ...value, shaft_flex: e.target.value || undefined })}
              disabled={false}
              className={getInputClass(`equipment_history.current_clubs_${clubType}.shaft_flex`, !value.shaft_flex)}
            >
              <option value="">Select</option>
              <option value="Ladies">Ladies</option>
              <option value="Senior">Senior</option>
              <option value="Regular">Regular</option>
              <option value="Stiff">Stiff</option>
              <option value="Extra Stiff">Extra Stiff</option>
              {clubType === 'wedge' && <option value="Wedge">Wedge Flex</option>}
            </select>
          </div>

          {/* Shaft Weight - ALWAYS EDITABLE */}
          <div>
            <label className="block text-base font-medium text-gray-700 mb-2">
              Shaft Weight (grams){renderPriorityIndicator(`equipment_history.current_clubs_${clubType}.shaft_weight`)}
            </label>
            <input
              type="number"
              min={clubType === 'driver' ? 40 : clubType === 'iron' ? 70 : 90}
              max={clubType === 'driver' ? 120 : 150}
              value={value.shaft_weight || ''}
              onChange={(e) => onChange({ ...value, shaft_weight: e.target.value ? parseInt(e.target.value) : undefined })}
              placeholder={clubType === 'driver' ? 'e.g., 65' : clubType === 'iron' ? 'e.g., 95' : 'e.g., 115'}
              disabled={false}
              className={getInputClass(`equipment_history.current_clubs_${clubType}.shaft_weight`, !value.shaft_weight)}
            />
          </div>
        </div>
      </div>

      {/* Auto-populated Delta Fields (Hidden - for ML scoring) */}
      {value.head_moi && (
        <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
          Auto-populated: MOI {value.head_moi}, CG {value.head_cg_height}/{value.head_cg_distance}
          {value.head_volume && `, Volume ${value.head_volume}cc`}
        </div>
      )}
    </div>
  );
};

export default EquipmentSelector;
